import "maplibre-gl/dist/maplibre-gl.css"

import { useEffect, useState, useRef } from "react"
import { getPlainText } from "utilities/getPlainText"

export default function Map({
  // default center is Cincinnati Metro Area
  lng: initialLng = -84.5929998,
  lat: initialLat = 39.0633933,
  zoom: initialZoom = 12,
  copyright,
  locations = [],
  hoveredLocation,
}) {
  const mapContainer = useRef(null)
  const map = useRef(null)

  const [lng] = useState(initialLng)
  const [lat] = useState(initialLat)
  const [zoom] = useState(initialZoom)
  const [markers, setMarkers] = useState([])

  useEffect(() => {
    if (map.current) return

    async function loadMap() {
      const maplibregl = (await import("maplibre-gl")).default
      const layers = (await import("components/mapLayersStyles")).default

      map.current = new maplibregl.Map({
        container: mapContainer.current,
        center: [lng, lat],
        minPitch: 0,
        minZoom: 6,
        maxZoom: 15,
        maxBounds: [
          [-90.06859729999553, 35.13173429003554], // Southwest coordinates
          [-80.01064528473512, 42.18509362955757], // Northeast coordinates
        ],
        pitch: 0,
        bearing: 20,
        scrollZoom: window.innerWidth > 1280 ? true : false,
        style: {
          version: 8,
          glyphs: "/gis/glyphs/{fontstack}/{range}.pbf",
          sources: {
            protomaps: {
              type: "vector",
              tiles: ["https://maps.2060.services/basemapsz15/{z}/{x}/{y}.mvt"],
              attribution: `${
                copyright ? `${copyright} | ` : ""
              }© <a href="https://openstreetmap.org">OpenStreetMap Contributors</a>`,
            },
          },
          layers,
        },
      })

      // markers/locations & popups
      const markers = locations.map(({ content: { map_pin, location_name, address }, slug }) => {
        const coords = [parseFloat(map_pin.lng), parseFloat(map_pin.lat)]
        const mapPin = document.createElement("div")
        mapPin.innerHTML =
          '<svg width="30" height="43" viewBox="0 0 30 43" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.7801 0C6.77097 0 0.28125 6.73635 0.28125 15.0499C0.28125 18.2993 1.29384 22.0261 2.99749 24.5095L14.7812 43L26.565 24.5095C28.2675 22.025 29.2812 18.3456 29.2812 15.0499C29.2812 6.73635 22.7915 0 14.7824 0H14.7801ZM14.7801 20.7838C11.7424 20.7838 9.25611 18.2043 9.25611 15.0499C9.25611 11.8955 11.7413 9.31593 14.7801 9.31593C17.819 9.31593 20.3042 11.8955 20.3042 15.0499C20.3042 18.2043 17.819 20.7838 14.7801 20.7838Z" fill="#BA672E"/><path d="M14.7801 20.7838C11.7424 20.7838 9.25611 18.2043 9.25611 15.0499C9.25611 11.8955 11.7413 9.31593 14.7801 9.31593V0C6.77097 0 0.28125 6.73635 0.28125 15.0499C0.28125 18.2993 1.29384 22.0261 2.99749 24.5095L14.7812 43V20.7838H14.7801Z" fill="#ed9b65"/></svg>'

        const textAddress = getPlainText(address?.[0]?.address) || map_pin.address || ""
        const popupBody = document.createElement("div")
        popupBody.innerHTML = `
        <h5 class="location-title text-m3 font-bold mb-2 leading-snug">${location_name}</h5>
        <address class="text-s2 not-italic mb-2.5">${textAddress}</address>
        <a href="#${slug}" class="bg-orange absolute bottom-0 right-0 p-2"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="8" fill="none"><path fill="white" d="M15.27 4.35a.5.5 0 0 0 0-.7L12.09.46a.5.5 0 1 0-.71.71L14.2 4l-2.83 2.83a.5.5 0 1 0 .7.7l3.19-3.18ZM.42 4.5h14.5v-1H.41v1Z"/></svg></a>
      `
        const markerHeight = 48,
          markerRadius = 18,
          linearOffset = 16
        const popup = new maplibregl.Popup({
          offset: {
            top: [0, 0],
            "top-left": [0, 0],
            "top-right": [0, 0],
            bottom: [0, -markerHeight],
            "bottom-left": [linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
            "bottom-right": [-linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
            left: [markerRadius, (markerHeight - markerRadius) * -1],
            right: [-markerRadius, (markerHeight - markerRadius) * -1],
          },
          closeOnMove: true,
          closeButton: false,
          focusAfterOpen: false,
          maxWidth: "280px",
          anchor: "bottom",
        }).setDOMContent(popupBody)

        const marker = new maplibregl.Marker({
          element: mapPin,
          anchor: "bottom",
        })
          .setLngLat(coords)
          .setPopup(popup)
          .addTo(map.current)

        mapPin.addEventListener("click", () => {
          map.current.flyTo({ center: coords, zoom: 15, speed: 1.8 })
          map.current.once("moveend", () => marker.togglePopup())
        })

        return marker
      })
      setMarkers(markers)

      // controls

      map.current.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-left")
      map.current.addControl(new maplibregl.FullscreenControl())
      map.current.addControl(
        new maplibregl.ScaleControl({
          unit: "imperial",
        }),
      )

      map.current.on("load", () => {
        map.current.flyTo({ center: [lng, lat], pitch: 40, zoom, bearing: 0, duration: 1000 })
        if (mapContainer.current) {
          mapContainer.current.classList.add("loaded")
        }
      })
    }

    loadMap()
  }, [lat, lng, zoom, locations, copyright, markers])

  useEffect(() => {
    if (!map.current) return
    if (hoveredLocation !== "" && hoveredLocation !== undefined) {
      const marker = markers.find((marker) => {
        const markerCoords = Object.values(marker.getLngLat()).toString()
        const hoveredLocationCoords = [
          hoveredLocation.content.map_pin.lng,
          hoveredLocation.content.map_pin.lat,
        ].toString()

        return markerCoords === hoveredLocationCoords
      })

      map.current.flyTo({
        center: [hoveredLocation.content.map_pin.lng, hoveredLocation.content.map_pin.lat],
        zoom: 15,
        speed: 1.0,
      })

      map.current.once("moveend", () => {
        if (marker) {
          marker.togglePopup()
        }
      })
    }
  }, [hoveredLocation, markers])

  return <div ref={mapContainer} className="aspect-[1140/545] h-full w-full" />
}
